import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";

export interface AuxiliaryType {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export const useAuxiliaryTypes = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: auxiliaryTypes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["auxiliary-types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("auxiliary_types")
        .select("*")
        .order("name");

      if (error) throw error;
      return data as AuxiliaryType[];
    },
  });

  const createAuxiliaryType = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from("auxiliary_types")
        .insert({ name })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auxiliary-types"] });
      toast({
        title: "Tipo de auxiliar creado",
        description: "El tipo de auxiliar ha sido creado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo crear el tipo de auxiliar.",
        variant: "destructive",
      });
    },
  });

  const updateAuxiliaryType = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await supabase
        .from("auxiliary_types")
        .update({ name, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auxiliary-types"] });
      toast({
        title: "Tipo de auxiliar actualizado",
        description: "El tipo de auxiliar ha sido actualizado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "No se pudo actualizar el tipo de auxiliar.",
        variant: "destructive",
      });
    },
  });

  const deleteAuxiliaryType = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("auxiliary_types")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auxiliary-types"] });
      toast({
        title: "Tipo de auxiliar eliminado",
        description: "El tipo de auxiliar ha sido eliminado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "No se pudo eliminar el tipo de auxiliar.",
        variant: "destructive",
      });
    },
  });

  return {
    auxiliaryTypes,
    isLoading,
    error,
    createAuxiliaryType: createAuxiliaryType.mutate,
    updateAuxiliaryType: updateAuxiliaryType.mutate,
    deleteAuxiliaryType: deleteAuxiliaryType.mutate,
    isCreating: createAuxiliaryType.isPending,
    isUpdating: updateAuxiliaryType.isPending,
    isDeleting: deleteAuxiliaryType.isPending,
  };
};
